<template>
  <Layout>
    <div data-component="VisitesList" :class="`w-full px-4 ${loading ? 'loading' : ''}`">
    <div v-if="!errored">
      <div class="list-header">
        <h3 :class="{ 'animate-blinking': totalLoading }">{{ mapTotalToTitle(total)(totalLoading) }}</h3>
        <ButtonFilter 
          :stringifiedFilters="stringifiedFilters" 
          :filtersNumber="filtersNumber" 
          :blink="loading"
          subject="MyVisites"
        />
      </div>
      <div class="list-wrapper">
        <ListHead 
          :columns="columns"
          :onSort="onSort"
          :loading="loading"
        />
        <ListBody 
          :rows="rows"
          :onAttribute="onAttribute"
          :onPlanify="onPlanify"
          :onResetPlanification="onResetPlanification"
          :onCheck="onCheck"
          :onDelete="onDelete"
          :onPostpone="onPostpone"
          :toggleDropdown="toggleDropdown"
          :closeDropdown="closeDropdown"
        />
      </div>
    </div>

    <WifiError v-if="errored" />
    
    <div class="bottom-overlay"></div>
    </div>
  </Layout>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import apiService from '../services/api'
import moment from 'moment'
import ButtonFilter from '../components/List/ButtonFilter.vue'
import ListHead from '../components/List/Head.vue'
import ListBody from '../components/List/Body.vue'
import WifiError from '../components/Common/WifiError.vue'
import AppLayout from '../components/Layout/AppLayout.vue'
import { buildUrlWithPreservedParams } from '../utils/urlHelper'
import { getFiltersWithDefaults } from '../services/filterStorageService'
import {
  getAttributionVisiteColumn,
  getScheduledVisitColumn,
  getPlanifiedColumn
} from '../utils/lot'

const SORT_NONE = 'None'
const SORT_DESC = 'Desc'

export default {
  name: 'VisitesListView',
  components: {
    ButtonFilter,
    ListHead,
    ListBody,
    WifiError,
    Layout: AppLayout
  },
  props: {
    mapTotalToTitle: {
      type: Function,
      default: (total) => (loading) => loading ? 'Chargement...' : `${total} visites`
    }
  },
  data() {
    return {
      loading: false,
      totalLoading: false,
      errored: false,
      total: 0,
      activeFilters: {},
      stringifiedFilters: '',
      filtersNumber: 0,
      currentUser: null,
      columns: [
        { label: 'Identifiant NSIL', sortId: 'nsil', sortable: true, direction: SORT_NONE, sortDexieId: ['nsil+id'] },
        { label: 'Code postal', sortId: 'zipCode', sortable: true, direction: SORT_NONE, sortDexieId: ['address.zipCode+id'] },
        { label: 'Type', sortId: 'propertyType', sortable: true, direction: SORT_NONE, sortDexieId: ['type+id'] },
        { label: 'Mandant', sortId: 'assistanceMandator', sortable: true, direction: SORT_NONE, sortDexieId: ['assistance.mandator.id+id'] },
        { label: 'Attribution visite', sortId: 'technicalManager', sortable: true, direction: SORT_NONE, sortDexieId: ['scheduledVisitStatusSortAsc+technicalManager.fullName+id'] },
        { label: 'A visiter avant', sortId: 'scheduledVisitAt', sortable: true, direction: 'Asc', sortDexieId: ['scheduledVisitAtSortAsc+scheduledVisitAt+scheduledVisitStatusSortAsc+scheduledVisitStatus+id'] },
        { label: 'Planifiée le', sortId: 'planifiedAt', sortable: true, direction: SORT_NONE, sortDexieId: ['occupancySortDesc+planifiedAtSortDesc+scheduledVisitStatusSortAsc+planifiedAt+id'] }
      ],
      rows: [],
      pagination: {
        page: 1,
        limit: 12,
        total: 0
      },
      progressiveLoadingEnabled: true,
      loadingBatchSize: 5,
      loadingDelay: 100,
      isLoadingMore: false,
      hasMoreData: true,
      scrollThreshold: 250,
      propertyTypes: {},
      visitStatuses: [],
      assistanceMandators: {},
      managerMandators: {}
    }
  },
  computed: {
    ...mapState('list', ['filters']),
    ...mapGetters('attribution', ['lastAttributionUpdate'])
  },
  async mounted() {
    await this.loadCurrentUser()
    // Charger les filtres depuis localStorage (avec defaults si première visite)
    this.activeFilters = getFiltersWithDefaults('MyVisites')
    // Assurer que le manager de l'utilisateur courant est toujours présent
    if (this.currentUser && this.currentUser.id && !this.activeFilters.manager) {
      const { saveFilters } = await import('../services/filterStorageService')
      this.activeFilters = { ...this.activeFilters, manager: this.currentUser.id }
      saveFilters('MyVisites', this.activeFilters)
    }
    await this.loadPropertyTypes()
    await this.loadVisitStatuses()
    await this.loadAssistanceMandators()
    await this.loadManagerMandators()
    await this.loadItems()
    this.setupScrollListener()
  },
  beforeUnmount() {
    this.removeScrollListener()
  },
  watch: {
    // Rechargement lors du retour depuis la page de filtres
    '$route'(to) {
      if (to.path === '/visites') {
        this.activeFilters = getFiltersWithDefaults('MyVisites')
        this.loadItems()
      }
    },
    lastAttributionUpdate(newVal) {
      if (newVal) {
        // Recharger les données quand une attribution a été modifiée
        this.loadItems()
      }
    }
  },
  methods: {
    ...mapActions('list', [
      'setItems', 
      'setLoading', 
      'setError', 
      'setPagination',
      'setTotalLoading',
      'setProgressiveLoading',
      'setReady',
      'updateRowLoading'
    ]),

    async loadCurrentUser() {
      try {
        const dexieService = (await import('../services/dexieService')).default
        this.currentUser = await dexieService.getUser()
        
        if (!this.currentUser) {
          console.warn('Aucun utilisateur connecté trouvé')
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'utilisateur connecté:', error)
        this.currentUser = null
      }
    },

    async loadPropertyTypes() {
      try {
        const dexieService = (await import('../services/dexieService')).default
        let cachedTypes = await dexieService.getDictionary('property_type')
        
        if (cachedTypes && Object.keys(cachedTypes).length > 0) {
          this.propertyTypes = cachedTypes
          return
        }
        
        this.propertyTypes = await apiService.getPropertyTypes()
      } catch (error) {
        this.propertyTypes = {}
      }
    },

    async loadVisitStatuses() {
      try {
        const dexieService = (await import('../services/dexieService')).default
        let cachedStatuses = await dexieService.getDictionary('visit_status')
        
        // Vérifier si les données sont dans la structure de l'ancienne app (avec items)
        if (cachedStatuses && cachedStatuses.items && Array.isArray(cachedStatuses.items)) {
          // Garder le format tableau { key, value }
          this.visitStatuses = cachedStatuses.items
          return
        }
        
        // Sinon, vérifier si c'est déjà un objet clé-valeur et le convertir en tableau
        if (cachedStatuses && Object.keys(cachedStatuses).length > 0) {
          this.visitStatuses = Object.entries(cachedStatuses).map(([key, value]) => ({ key, value }))
          return
        }
        
        this.visitStatuses = []
      } catch (error) {
        console.error('Erreur lors du chargement des visitStatuses:', error)
        this.visitStatuses = []
      }
    },

    async loadAssistanceMandators() {
      try {
        const { assistanceMandators } = await import('../services/dexieService')
        
        // Récupérer tous les assistanceMandators depuis Dexie
        const mandators = await assistanceMandators().toArray()
        
        // Créer une map pour un accès rapide par ID
        const mandatorsMap = {}
        mandators.forEach(mandator => {
          if (mandator.id && mandator.name) {
            mandatorsMap[mandator.id] = mandator.name
          }
        })
        
        this.assistanceMandators = mandatorsMap
      } catch (error) {
        console.error('Erreur lors du chargement des assistanceMandators:', error)
        this.assistanceMandators = {}
      }
    },

    async loadManagerMandators() {
      try {
        const { managerMandators } = await import('../services/dexieService')
        
        // Récupérer tous les managerMandators depuis Dexie
        const mandators = await managerMandators().toArray()
        
        // Créer une map pour un accès rapide par ID avec nom et division
        const mandatorsMap = {}
        mandators.forEach(mandator => {
          if (mandator.id) {
            mandatorsMap[mandator.id] = {
              name: mandator.name || '',
              division: mandator.division || ''
            }
          }
        })
        
        this.managerMandators = mandatorsMap
      } catch (error) {
        console.error('Erreur lors du chargement des managerMandators:', error)
        this.managerMandators = {}
      }
    },

    async loadItems(isLoadMore = false) {
      if (!isLoadMore) {
        this.resetPagination()
        this.loading = true
        this.totalLoading = true
        this.errored = false
      } else {
        if (this.isLoadingMore) return
        this.isLoadingMore = true
      }
      
      try {
        const dexieService = (await import('../services/dexieService')).default
        
        if (!isLoadMore) {
          this.stringifiedFilters = new URLSearchParams(this.activeFilters).toString()
          this.filtersNumber = Object.keys(this.activeFilters).length
        }
        
        let items = []
        let total = 0
        
        if (isLoadMore) {
          // Pour le chargement de plus d'éléments, ajouter des placeholders
          const loadingPlaceholders = this.createLoadingPlaceholders(this.pagination.limit)
          this.rows = [...this.rows, ...loadingPlaceholders]
        }
        
        // Récupérer toutes les propriétés visitables
        let allVisitableProperties = await this.getVisitablePropertiesOptimized(dexieService)
        
        // Appliquer le tri sur toutes les données
        const activeColumn = this.columns.find(col => col.direction !== SORT_NONE)
        if (activeColumn) {
          allVisitableProperties = this.sortAllProperties(allVisitableProperties, activeColumn.sortId, activeColumn.direction)
        }
        
        total = allVisitableProperties.length
        
        // Calculer la page actuelle
        const currentPage = isLoadMore ? Math.floor(this.rows.filter(row => !row.loading).length / this.pagination.limit) + 1 : 1
        
        // Appliquer la pagination manuelle
        const startIndex = (currentPage - 1) * this.pagination.limit
        const endIndex = startIndex + this.pagination.limit
        items = allVisitableProperties.slice(startIndex, endIndex)
        
        this.hasMoreData = endIndex < total
        
        if (!isLoadMore) {
          this.total = total
        }
        
        if (isLoadMore) {
          // Nettoyer les placeholders de chargement
          this.rows = this.rows.filter(row => !row.loading)
        }
        
        if (items.length > 0) {
          if (this.progressiveLoadingEnabled) {
            await this.loadItemsProgressively(items, isLoadMore)
          } else {
            const newRows = this.transformItemsToRows(items)
            if (isLoadMore) {
              this.rows = [...this.rows, ...newRows]
            } else {
              this.rows = newRows
            }
          }
        }
        
        if (!isLoadMore) {
          this.pagination = {
            page: 1,
            limit: this.pagination.limit || 12,
            total: total
          }
          
          this.setItems(allVisitableProperties)
          this.setPagination(this.pagination)
        } else {
          this.hasMoreData = items.length === this.pagination.limit && this.rows.length < this.total
        }
        
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
        if (!isLoadMore) {
          this.errored = true
          this.setError(error.message)
        } else {
          this.rows = this.rows.filter(row => !row.loading)
        }
      } finally {
        if (!isLoadMore) {
          this.loading = false
          this.totalLoading = false
          this.setLoading(false)
        } else {
          this.isLoadingMore = false
        }
      }
    },

    // Méthode optimisée pour récupérer seulement les propriétés visitables attribuées à l'utilisateur connecté
    async getVisitablePropertiesOptimized(dexieService) {
      try {
        await dexieService.init()
        const allProperties = await dexieService.getAllProperties()
        
        // Filtrer les propriétés visitables
        let visitableProperties = allProperties.filter(property => 
          property.scheduledVisitStatus !== null && property.scheduledVisitStatus !== undefined
        )
        
        // Filtrer par utilisateur connecté si disponible
        if (this.currentUser && this.currentUser.id) {
          visitableProperties = visitableProperties.filter(property =>
            property.technicalManager?.id === this.currentUser.id
          )
        } else {
          console.warn('Aucun utilisateur connecté - affichage de toutes les visites')
        }
        
        return visitableProperties
      } catch (error) {
        console.error('Erreur lors de la récupération optimisée des propriétés visitables:', error)
        return []
      }
    },

    async loadItemsProgressively(items, isLoadMore = false) {
      if (isLoadMore) {
        // Pour le chargement de plus d'éléments, ajouter des placeholders à la fin
        const placeholders = this.createLoadingPlaceholders(items.length)
        this.rows = [...this.rows, ...placeholders]
      } else {
        // Pour le chargement initial, remplacer tout par des placeholders
        this.rows = this.createLoadingPlaceholders(items.length)
      }
      
      for (let i = 0; i < items.length; i += this.loadingBatchSize) {
        const batch = items.slice(i, i + this.loadingBatchSize)
        const batchRows = this.transformItemsToRows(batch)
        
        batchRows.forEach((row, index) => {
          let globalIndex
          if (isLoadMore) {
            // Pour le chargement de plus d'éléments, calculer l'index à partir de la fin
            globalIndex = this.rows.length - items.length + i + index
          } else {
            // Pour le chargement initial
            globalIndex = i + index
          }
          
          if (globalIndex < this.rows.length && globalIndex >= 0) {
            this.rows[globalIndex] = row
          }
        })
        
        if (i + this.loadingBatchSize < items.length) {
          await this.delay(this.loadingDelay)
        }
      }
    },

    createLoadingPlaceholders(count) {
      const placeholders = []
      for (let i = 0; i < count; i++) {
        placeholders.push({
          id: `loading-${i}`,
          checked: false,
          loading: true,
          data: this.createLoadingRowData(),
          dropdown: {
            open: false,
            actions: []
          }
        })
      }
      return placeholders
    },

    createLoadingRowData() {
      const columnCount = this.columns.length
      const loadingData = []
      
      for (let i = 0; i < columnCount; i++) {
        loadingData.push([
          { type: 'TextLoading', value: this.getLoadingPlaceholderClass(i) }
        ])
      }
      
      return loadingData
    },

    getLoadingPlaceholderClass(columnIndex) {
      const classes = ['short', 'medium', 'long', 'short']
      return classes[columnIndex % classes.length] || 'medium'
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    transformItemsToRows(items) {
      // Optimisation : traitement par batch pour éviter les blocages
      return items.map(item => {
        // Pré-calculer les valeurs fréquemment utilisées
        const address = item.address || {}
        const technicalManager = item.technicalManager || {}
        const assistance = item.assistance || {}
        const manager = item.manager || {}
        const legalOwner = item.legalOwner || {}
        
        return {
          id: item.id,
          checked: false,
          loading: false,
          propertyTeams: item.teams || [], // Ajouter les équipes de la propriété
          data: this.getRowDataOptimized(item, {
            address,
            technicalManager,
            assistance,
            manager,
            legalOwner
          }),
          dropdown: {
            open: false,
            actions: [{ type: 'Postpone' }]
          }
        }
      })
    },

    getRowDataOptimized(item, precomputed) {
      const { address, assistance, manager, legalOwner } = precomputed
      
      // Pré-calculer les valeurs pour éviter les appels répétés
      const propertyType = this.getPropertyTypeOptimized(item.type)
      const assistanceMandatorName = this.getAssistanceMandatorOptimized(assistance)
      
      return [
        // Colonne 1: Identifiant NSIL
        [
          { type: 'TextBoldBlue', value: item.nsil || item.propertyCode || `Lot ${item.id}`, link: buildUrlWithPreservedParams(`/dossier/${item.id}/fiche/${item.id}`, this.$route) },
          { type: 'Text', value: `Immeuble - ${item.propertyCode || ''}` },
          { type: 'Text', value: `Lot - ${item.unitCode || ''}` }
        ],
        // Colonne 2: Code postal
        [
          { type: 'TextBold', value: address.zipCode || '' },
          { type: 'Text', value: address.street || '' },
          { type: 'TextBold', value: address.city || '' }
        ],
        // Colonne 3: Type
        [
          { type: 'TextBold', value: propertyType },
          { type: 'Text', value: item.occupancy || '' }
        ],
        // Colonne 4: Mandant
        [
          { type: 'TextBold', value: assistanceMandatorName },
          { type: 'Text', value: this.getManagerMandatorOptimized(manager) },
          { type: 'Text', value: this.getLegalOwnerWithDivisionOptimized(legalOwner) }
        ],
        // Colonne 5: Attribution visite
        this.getAttributionVisiteColumnOptimized(item),
        // Colonne 6: A visiter avant
        this.getScheduledVisitColumnOptimized(item),
        // Colonne 7: Planifiée le
        this.getPlanifiedColumnOptimized(item)
      ]
    },

    getPropertyTypeOptimized(typeCode) {
      if (!typeCode) return 'Non défini'
      
      if (this.propertyTypes && this.propertyTypes[typeCode]) {
        return this.propertyTypes[typeCode]
      }
      
      return typeCode
    },

    getAssistanceMandatorOptimized(assistance) {
      const mandatorId = assistance?.mandator?.id
      if (!mandatorId) return assistance?.mandator?.name || '-'
      
      // Chercher le nom dans la table assistanceMandators
      const mandatorName = this.assistanceMandators[mandatorId]
      // Fallback sur le nom stocké directement dans la propriété (ex: sync depuis MSSQL)
      return mandatorName || assistance?.mandator?.name || '-'
    },

    getManagerMandatorOptimized(manager) {
      const mandatorId = manager?.mandator?.id
      if (!mandatorId) return null
      
      // Chercher le nom et division dans la table managerMandators
      const mandatorData = this.managerMandators[mandatorId]
      if (!mandatorData) return null
      
      const name = mandatorData.name || ''
      const division = mandatorData.division || ''
      
      // Si pas de nom, retourner null
      if (!name) return division || null
      
      // Si pas de division, retourner juste le nom
      if (!division) return name
      
      // Si le nom contient déjà la division, ne pas la répéter
      if (name.includes(division)) {
        return name
      }
      
      // Sinon retourner "Nom - Division"
      return `${name} - ${division}`
    },

    getLegalOwnerWithDivisionOptimized(legalOwner) {
      if (!legalOwner) return null
      
      const name = legalOwner.name || ''
      const division = legalOwner.division || ''
      
      // Si pas de nom, retourner null
      if (!name) return division || null
      
      // Si pas de division, retourner juste le nom
      if (!division) return name
      
      // Si nom et division sont identiques, ne pas répéter
      if (name === division) {
        return name
      }
      
      // Si le nom contient déjà la division, ne pas la répéter
      if (name.includes(division)) {
        return name
      }
      
      // Sinon retourner "Nom - Division"
      return `${name} - ${division}`
    },

    getAttributionVisiteColumnOptimized(item) {
      return getAttributionVisiteColumn(item)
    },

    getScheduledVisitColumnOptimized(item) {
      return getScheduledVisitColumn(item, this.visitStatuses)
    },

    getPlanifiedColumnOptimized(item) {
      return getPlanifiedColumn(item)
    },

    getDelegationTextOptimized(item, technicalManager) {
      if (!item.teams || item.teams.length === 0) {
        return ''
      }
      
      const managerTeam = technicalManager.team
      const isManagerInTeams = item.teams.some(team => team.id === managerTeam?.id)
      
      if (!isManagerInTeams) {
        const firstTeamName = item.teams[0].name.replace(/Groupe/i, 'gpe')
        const suffix = item.teams.length > 1 ? ', ...' : ''
        return `Délégation ${firstTeamName}${suffix}`
      }
      
      return ''
    },

    formatDateOptimized(date) {
      if (!date) return ''
      return moment(date).format('DD/MM/YYYY')
    },

    // Méthodes utilitaires pour extraire les données
    getZipCode(item) {
      return item.address?.zipCode || ''
    },

    getStreet(item) {
      return item.address?.street || ''
    },

    getCity(item) {
      return item.address?.city || ''
    },

    getPropertyType(item) {
      const typeCode = item.type || ''
      
      if (this.propertyTypes && this.propertyTypes[typeCode]) {
        return this.propertyTypes[typeCode]
      }
      
      return typeCode || 'Non défini'
    },

    getAssistanceMandator(item) {
      const mandatorId = item.assistance?.mandator?.id
      if (!mandatorId) return item.assistance?.mandator?.name || '-'
      
      // Chercher le nom dans la table assistanceMandators
      const mandatorName = this.assistanceMandators[mandatorId]
      // Fallback sur le nom stocké directement dans la propriété (ex: sync depuis MSSQL)
      return mandatorName || item.assistance?.mandator?.name || '-'
    },

    getManagerMandator(item) {
      const mandatorId = item.manager?.mandator?.id
      if (!mandatorId) return null
      
      // Chercher le nom et division dans la table managerMandators
      const mandatorData = this.managerMandators[mandatorId]
      if (!mandatorData) return null
      
      const name = mandatorData.name || ''
      const division = mandatorData.division || ''
      
      // Si pas de nom, retourner null
      if (!name) return division || null
      
      // Si pas de division, retourner juste le nom
      if (!division) return name
      
      // Si le nom contient déjà la division, ne pas la répéter
      if (name.includes(division)) {
        return name
      }
      
      // Sinon retourner "Nom - Division"
      return `${name} - ${division}`
    },

    getLegalOwnerWithDivision(item) {
      const legalOwner = item.legalOwner
      if (!legalOwner) return null
      
      const name = legalOwner.name || ''
      const division = legalOwner.division || ''
      
      // Si pas de nom, retourner null
      if (!name) return division || null
      
      // Si pas de division, retourner juste le nom
      if (!division) return name
      
      // Si nom et division sont identiques, ne pas répéter
      if (name === division) {
        return name
      }
      
      // Si le nom contient déjà la division, ne pas la répéter
      if (name.includes(division)) {
        return name
      }
      
      // Sinon retourner "Nom - Division"
      return `${name} - ${division}`
    },

    getAttributionVisiteColumn(item) {
      if (this.isVisitableProperty(item.scheduledVisitStatus)) {
        if (item.technicalManager?.id) {
          return [
            {
              type: 'TextBold',
              value: item.technicalManager.fullName || 
                     `${item.technicalManager.firstName || ''} ${item.technicalManager.lastName || ''}`.trim(),
              link: `/attribuer?lots[]=${item.id}&team=${item.technicalManager.team?.id || ''}&manager=${item.technicalManager.id}`
            },
            { type: 'Text', value: item.technicalManager.team?.name || '-' },
            { type: 'Text', value: this.getDelegationText(item) }
          ]
        } else {
          return [
            { type: 'Attribute', value: item.technicalManager }
          ]
        }
      }
      return []
    },

    getScheduledVisitColumn(item) {
      if (item.scheduledVisitStatus) {
        return [{ type: 'ScheduledAt', value: this.getVisitStatusValue(item.scheduledVisitStatus) }]
      } else if (item.scheduledVisitAt) {
        return [{ type: 'ScheduledAt', value: this.formatDate(item.scheduledVisitAt) }]
      }
      return []
    },

    getPlanifiedColumn(item) {
      if (this.isVisitableProperty(item.scheduledVisitStatus)) {
        return [{ type: 'Planify', value: item.planifiedAt }]
      }
      return []
    },

    isVisitableProperty(scheduledVisitStatus) {
      return scheduledVisitStatus !== null && scheduledVisitStatus !== undefined
    },

    getDelegationText(item) {
      if (!item.teams || item.teams.length === 0) {
        return ''
      }
      
      const managerTeam = item.technicalManager?.team
      const isManagerInTeams = item.teams.some(team => team.id === managerTeam?.id)
      
      if (!isManagerInTeams) {
        const firstTeamName = item.teams[0].name.replace(/Groupe/i, 'gpe')
        const suffix = item.teams.length > 1 ? ', ...' : ''
        return `Délégation ${firstTeamName}${suffix}`
      }
      
      return ''
    },

    getVisitStatusValue(scheduledVisitStatus) {
      return scheduledVisitStatus || ''
    },

    formatDate(date) {
      if (!date) return ''
      return moment(date).format('DD/MM/YYYY')
    },

    sortAllProperties(properties, sortId, direction) {
      const multiplier = direction === SORT_DESC ? -1 : 1
      
      return [...properties].sort((a, b) => {
        let valueA, valueB
        
        switch (sortId) {
          case 'nsil':
            valueA = a.nsil || ''
            valueB = b.nsil || ''
            break
            
          case 'zipCode':
            valueA = a.address?.zipCode || ''
            valueB = b.address?.zipCode || ''
            break
            
          case 'propertyType':
            valueA = this.getPropertyType(a)
            valueB = this.getPropertyType(b)
            break
            
          case 'assistanceMandator':
            valueA = this.getAssistanceMandator(a)
            valueB = this.getAssistanceMandator(b)
            break
            
          case 'technicalManager':
            valueA = a.technicalManager?.fullName || ''
            valueB = b.technicalManager?.fullName || ''
            break
            
          case 'scheduledVisitAt': {
            // Utiliser les valeurs pré-calculées qui prennent en compte le statut ET la date
            if (direction === SORT_DESC) {
              valueA = a.scheduledVisitAtSortDesc !== undefined ? a.scheduledVisitAtSortDesc : 0
              valueB = b.scheduledVisitAtSortDesc !== undefined ? b.scheduledVisitAtSortDesc : 0
            } else {
              valueA = a.scheduledVisitAtSortAsc !== undefined ? a.scheduledVisitAtSortAsc : Number.MAX_SAFE_INTEGER
              valueB = b.scheduledVisitAtSortAsc !== undefined ? b.scheduledVisitAtSortAsc : Number.MAX_SAFE_INTEGER
            }
            
            // Comparer les valeurs pré-calculées
            if (valueA < valueB) return -1
            if (valueA > valueB) return 1
            
            // En cas d'égalité, trier par ID
            if (a.id < b.id) return -1
            if (a.id > b.id) return 1
            return 0
          }
            
          case 'planifiedAt': {
            // Gérer le tri par date avec les dates vides en dernier
            valueA = a.planifiedAt || null
            valueB = b.planifiedAt || null
            
            // Les valeurs null vont à la fin
            if (valueA === null && valueB === null) return 0
            if (valueA === null) return 1
            if (valueB === null) return -1
            
            // Comparer les dates
            const dateA = new Date(valueA)
            const dateB = new Date(valueB)
            return (dateA - dateB) * multiplier
          }
            
          default:
            return 0
        }
        
        // Comparaison générique pour les valeurs string
        if (sortId !== 'scheduledVisitAt' && sortId !== 'planifiedAt') {
          if (valueA < valueB) return -1 * multiplier
          if (valueA > valueB) return 1 * multiplier
          return 0
        }
        
        return 0
      })
    },

    onSort(direction) {
      return (sortId) => {
        // Mettre à jour l'état des colonnes
        this.columns = this.columns.map(col => ({
          ...col,
          direction: col.sortId === sortId ? direction : SORT_NONE
        }))
        
        // Recharger les données avec le nouveau tri
        this.loadItems()
      }
    },

    onAttribute() {
      return () => {
        // TODO: Implement attribute logic
      }
    },

    onPlanify() {
      return () => {
        // TODO: Implement planify logic
      }
    },

    onResetPlanification() {
      return () => {
        // TODO: Implement reset planification logic
      }
    },

    onCheck(id) {
      return () => {
        this.rows = this.rows.map(row => 
          row.id === id ? { ...row, checked: !row.checked } : row
        )
      }
    },

    onDelete() {
      return () => {
        // TODO: Implement delete logic
      }
    },

    onPostpone() {
      return () => {
        // TODO: Implement postpone logic
      }
    },

    toggleDropdown(id) {
      return () => {
        this.rows = this.rows.map(row => ({
          ...row,
          dropdown: {
            ...row.dropdown,
            open: row.id === id ? !row.dropdown.open : false
          }
        }))
      }
    },

    closeDropdown() {
      return () => {
        this.rows = this.rows.map(row => ({
          ...row,
          dropdown: {
            ...row.dropdown,
            open: false
          }
        }))
      }
    },

    // Méthodes pour la pagination par scroll infini
    setupScrollListener() {
      window.addEventListener('scroll', this.handleScroll)
    },

    removeScrollListener() {
      window.removeEventListener('scroll', this.handleScroll)
    },

    handleScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight

      const distanceFromBottom = documentHeight - (scrollTop + windowHeight)

      if (distanceFromBottom < this.scrollThreshold && this.shouldLoadMore()) {
        this.loadMoreItems()
      }
    },

    shouldLoadMore() {
      return (
        !this.isLoadingMore && 
        !this.loading && 
        this.hasMoreData && 
        this.rows.length < this.total &&
        this.rows.filter(row => !row.loading).length > 0
      )
    },

    async loadMoreItems() {
      await this.loadItems(true)
    },

    async loadNewItemsProgressively(newRows) {
      const placeholders = this.createLoadingPlaceholders(newRows.length)
      this.rows = [...this.rows, ...placeholders]

      for (let i = 0; i < newRows.length; i += this.loadingBatchSize) {
        const batch = newRows.slice(i, i + this.loadingBatchSize)
        
        batch.forEach((row, index) => {
          const globalIndex = this.rows.length - newRows.length + i + index
          if (globalIndex < this.rows.length) {
            this.rows[globalIndex] = row
          }
        })
        
        if (i + this.loadingBatchSize < newRows.length) {
          await this.delay(this.loadingDelay)
        }
      }
    },

    resetPagination() {
      this.rows = []
      this.pagination.page = 1
      this.hasMoreData = true
      this.isLoadingMore = false
    }
  }
}
</script>

<style lang="scss">
@use './../assets/styles/components/List.scss';
</style>
